import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Camera, Link2, Eye, Check, ArrowRight,
  Send, ChevronRight, Sparkles,
} from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";

const SAMPLE_TOKEN = "CurbPourDemo";

// Internal cross-links (SEO crawl depth) — rendered only on these vertical pages,
// never in the shared SiteFooter, so the home page is untouched.
const TRADE_LINKS = [
  { to: "/punch-list-contractors", label: "General contractors" },
  { to: "/drywall-punch-list", label: "Drywall" },
  { to: "/painting-punch-list", label: "Painting" },
  { to: "/electrical-punch-list", label: "Electrical" },
  { to: "/restoration-punch-list", label: "Restoration" },
  { to: "/multifamily-turns", label: "Multifamily turns" },
  { to: "/make-ready", label: "Make-ready unit turns" },
  { to: "/realtor-repairs", label: "Realtor repairs" },
];

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5 transition-all duration-300 hover:-translate-y-1 hover:ring-emerald-500/40 hover:shadow-emerald-500/10";

function PhoneMock({ chipTop = "12 opens", chipSub = "Last from 🇺🇸 Austin, TX · 2 min ago" }) {
  return (
    <div className="relative mx-auto w-[280px] sm:w-[320px]">
      <div className="rounded-[2.6rem] border border-border bg-[hsl(222,44%,10%)] p-2.5 shadow-2xl ring-1 ring-white/5">
        <div className="overflow-hidden rounded-[2rem] bg-[hsl(222,47%,8%)]">
          <img src="/shots/jobdetail_mobile.webp" alt="Send-Proof job with its punch-list reports" className="block w-full" loading="eager" />
        </div>
      </div>
      <div className="absolute -bottom-4 -right-3 flex items-center gap-2.5 rounded-2xl border border-border bg-card px-4 py-3 shadow-xl sm:-right-8">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15">
          <Eye className="h-5 w-5 text-emerald-500" />
        </div>
        <div>
          <div className="text-sm font-bold leading-none">{chipTop}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">{chipSub}</div>
        </div>
      </div>
    </div>
  );
}

const VERTICALS = {
  contractors: {
    title: "Punch List App for Contractors with Photo Proof | Send-Proof",
    eyebrow: "Punch Lists · Photo Proof · Open Tracking",
    h1a: "Stop driving to the site to see if the trade ",
    h1b: "finished.",
    sub: "Send each trade only their items. Get a proof photo when it's done. See the second they open the link — no app for them to install.",
    subMuted: "Built for residential GCs, custom builders, remodelers and punch-out supers.",
    desc: "Send-Proof for GCs and remodelers: send each trade only their punch list items, get a photo when done, and see the moment they open the link.",
    heroBullets: ["Split one messy list by trade", "Photo proof on every item", "Live open receipt per link"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "Build the punch list", d: "Add every item that needs doing — notes and reference photos included." },
      { t: "Photo each item", d: "On-site, the trade snaps and annotates proof for every item, then submits." },
      { t: "Send the link", d: "One link to the client, GC or sub — and you see the moment it's opened." },
    ],
    sortTitle: "One messy punch list in. Clean per-trade sends out.",
    sortSub: "Got 40 items across every trade? Tap once and AI sorts them into buckets — Electrical, Plumbing, Drywall — matched to your vendors. Send each trade its own list with photos and a live checklist link.",
    faq: [
      { q: "Do the subs need an app or an account?", a: "No. They tap the text-message link, see only their items, snap photos, and submit. Nothing to download, nothing to log into." },
      { q: "Can I send each trade only their items?", a: "Yes. Sort & Send uses AI to split one punch list into per-trade buckets and sends each trade a clean list with just their scope." },
      { q: "How do I know they even saw it?", a: "You'll see when the link's been opened, so you know it landed — no more guessing. But the real signal is the photo proof that comes back: your crew shoots each item and you're pinged the moment they send it." },
      { q: "How do I know the work is actually done?", a: "A proof photo comes back on every item, annotated right on the image, with a timestamp you can stand behind." },
    ],
  },
  makeReady: {
    title: "Make-Ready Punch List App with Photo Proof | Send-Proof",
    eyebrow: "Unit Turns · Photo Proof · Open Tracking",
    h1a: "Inspect at 9. Crews have their list by ",
    h1b: "9:15.",
    sub: "Turn a walkthrough into a make-ready punch list, send each crew only their items, and watch vacancy days drop as photo proof rolls back in.",
    subMuted: "Built for property managers and make-ready supervisors running unit turns.",
    desc: "Send-Proof for unit turns: turn a walkthrough into a make-ready punch list, send each crew their items, and get photo proof back to cut vacancy days.",
    heroBullets: ["Walkthrough to punch list on the spot", "Photo proof per unit, per item", "See the moment a crew opens it"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "Walk the unit", d: "Turn your make-ready walkthrough into a punch list right there — notes and reference photos per item." },
      { t: "Photo each item", d: "Paint, clean, repair — each crew snaps annotated proof for their items and submits." },
      { t: "Send the link", d: "One link per crew or vendor — and you see the moment it's opened, no app for them to install." },
    ],
    sortTitle: "One turn list in. Clean per-crew sends out.",
    sortSub: "A full unit turn spans paint, clean, repair, punch. Tap once and AI sorts every item into the right crew — matched to your vendors — so each crew gets only their scope with photos and a live checklist link.",
    faq: [
      { q: "Do turn crews need an app or an account?", a: "No. Crews tap the link, see only their items for that unit, snap photos, and submit. No download, no login." },
      { q: "Can I send each crew only their scope?", a: "Yes. Sort & Send splits a unit-turn list into per-crew buckets — paint, clean, repair — and sends each their own list." },
      { q: "How do I cut vacancy days?", a: "You stop chasing status. The moment a crew opens the link you know it's in motion, and photo proof tells you it's ready to re-list — no drive-by inspection." },
      { q: "How do I prove the unit is ready?", a: "A timestamped proof photo lands on every item, annotated on the image — a clean record for owners and the next tenant." },
    ],
  },
  realtors: {
    title: "Realtor Repair Punch List with Photo Proof | Send-Proof",
    eyebrow: "Listing Repairs · Photo Proof · Open Tracking",
    h1a: "Send the handyman the list. Get photos before ",
    h1b: "closing.",
    sub: "List every repair, send the link to your handyman or vendor, and get timestamped photo proof each item's done — without driving back to the property.",
    subMuted: "Built for realtors and listing agents prepping homes and clearing closing punch-outs.",
    desc: "Send-Proof for realtors: list the repairs, text your handyman the link, and get timestamped photo proof on each item before the walkthrough or closing.",
    heroBullets: ["Repair list to link in minutes", "Photo proof before the walkthrough", "See the moment your vendor opens it"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "List the repairs", d: "Every item from the inspection or listing prep — notes and reference photos per item." },
      { t: "Photo each item", d: "Your handyman or vendor snaps annotated proof for every repair and submits." },
      { t: "Send the link", d: "One link to the vendor, seller or closing agent — and you see the moment it's opened." },
    ],
    sortTitle: "One repair list in. Clean per-vendor sends out.",
    sortSub: "Inspection came back with 20 fixes across handyman, electrician and plumber? Tap once and AI sorts them by vendor so each gets only their items — with photos and a live checklist link you can forward to the seller or closing agent.",
    faq: [
      { q: "Does my handyman need an app or account?", a: "No. They tap the link, see only their repair items, snap photos, and submit. Nothing to download or log into." },
      { q: "Can I forward proof to the seller or closing agent?", a: "Yes. The same live link shows every repair with a timestamped photo — clean documentation for closing or the buyer's walkthrough." },
      { q: "How do I know the vendor saw the list?", a: "You'll see the moment the link's opened, so nothing sits unseen — and when the vendor sends photo proof back, you're notified instantly so closing never stalls." },
      { q: "How do I prove repairs are done?", a: "A timestamped proof photo lands on every item, annotated on the image — proof you can stand behind at the closing table." },
    ],
  },
  drywall: {
    title: "Drywall Punch List App with Photo Proof | Send-Proof",
    eyebrow: "Drywall Punch-Outs · Photo Proof · Open Tracking",
    h1a: "Stop driving back to check if the drywall crew ",
    h1b: "patched it.",
    sub: "Send your drywall crew their punch-out list, get a proof photo on every patch, tape and sand, and see the second they open the link — no app for them.",
    subMuted: "Built for drywall subs, GCs and punch-out supers closing out hang, tape and finish.",
    desc: "Send-Proof for drywall: text your crew the punch-out list, get a photo on every patch, tape and sand, and see the moment the link opens. No app for the crew.",
    heroBullets: ["Every patch, tape & sand item", "Photo proof before paint goes on", "Live open receipt per link"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "List the punch-outs", d: "Nail pops, bad seams, corner beads, sand marks — add every drywall item with a reference photo." },
      { t: "Photo each fix", d: "The crew snaps annotated proof on every patch and finish before they leave the wall." },
      { t: "Send the link", d: "One link to the sub or GC — and you see the moment it's opened, no download." },
    ],
    sortTitle: "One wall-by-wall list in. Clean crew sends out.",
    sortSub: "Punch-outs scattered across floors and trades? Tap once and AI buckets drywall, paint and trim to the right crew — matched to your vendors — so each gets only their scope with photos and a live checklist link.",
    faq: [
      { q: "Does my drywall crew need an app?", a: "No. They tap the text link, see only their patches, snap photos, and submit. Nothing to download or log into." },
      { q: "Can I prove it's ready for paint?", a: "Yes. A timestamped proof photo lands on every patch and sand — so paint never goes on over a missed seam." },
      { q: "How do I know they saw the list?", a: "You see the moment the link opens, and you're pinged the second a proof photo comes back." },
      { q: "Can I send just the drywall scope?", a: "Yes. Sort & Send splits a mixed punch list so your drywall crew gets only their items." },
    ],
  },
  painting: {
    title: "Painting Punch List App with Photo Proof | Send-Proof",
    eyebrow: "Paint Punch-Outs · Photo Proof · Open Tracking",
    h1a: "Stop driving back to see if the painters ",
    h1b: "touched it up.",
    sub: "Send painters their touch-up and holiday list, get a proof photo on every wall and trim line, and see the second they open the link — no app for them.",
    subMuted: "Built for painting subs, GCs and supers closing out cut-ins, holidays and touch-ups.",
    desc: "Send-Proof for painting: text painters the touch-up list, get a photo on every wall, trim and cut-in, and see when the link opens. No app for the crew.",
    heroBullets: ["Every holiday, drip & cut-in", "Photo proof on every wall & trim", "Live open receipt per link"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "List the touch-ups", d: "Holidays, drips, bad cut-ins, trim lines — add every paint item with a reference photo." },
      { t: "Photo each wall", d: "Painters snap annotated proof on every touch-up and coat before they pack up." },
      { t: "Send the link", d: "One link to the painter or GC — and you see the moment it's opened, no download." },
    ],
    sortTitle: "One walkthrough list in. Clean painter sends out.",
    sortSub: "Punch items across paint, drywall and trim? Tap once and AI buckets each to the right crew — matched to your vendors — so painters get only their walls with photos and a live checklist link.",
    faq: [
      { q: "Do painters need an app or account?", a: "No. They tap the link, see only their touch-ups, snap photos, and submit. Nothing to download." },
      { q: "Can I prove the final coat is done?", a: "Yes. A timestamped proof photo lands on every wall and trim line — proof the touch-ups got handled." },
      { q: "How do I know they opened it?", a: "You see the moment the link opens and get pinged the second proof comes back." },
      { q: "Can I send only the paint scope?", a: "Yes. Sort & Send splits a mixed list so painters get just their items." },
    ],
  },
  electrical: {
    title: "Electrical Punch List App with Photo Proof | Send-Proof",
    eyebrow: "Electrical Punch-Outs · Photo Proof · Open Tracking",
    h1a: "Stop driving back to see if the electrician ",
    h1b: "trimmed it out.",
    sub: "Send electricians their trim-out and punch list, get a proof photo on every device, cover and fixture, and see the second they open the link — no app for them.",
    subMuted: "Built for electrical subs, GCs and supers closing out trim-out, devices and fixtures.",
    desc: "Send-Proof for electrical: text the trim-out punch list, get a photo on every device, cover and fixture, and see when the link opens. No app for the crew.",
    heroBullets: ["Every device, cover & fixture", "Photo proof on the trim-out", "Live open receipt per link"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "List the trim-out", d: "Missing covers, swapped devices, fixture hangs, labeling — add every item with a reference photo." },
      { t: "Photo each device", d: "The electrician snaps annotated proof on every outlet, switch and fixture before leaving." },
      { t: "Send the link", d: "One link to the sub or GC — and you see the moment it's opened, no download." },
    ],
    sortTitle: "One punch list in. Clean electrical send out.",
    sortSub: "Items spread across electrical, plumbing and drywall? Tap once and AI buckets each to the right trade — matched to your vendors — so the electrician gets only their devices with photos and a live checklist link.",
    faq: [
      { q: "Does the electrician need an app?", a: "No. They tap the link, see only their devices and fixtures, snap photos, and submit. No download or login." },
      { q: "Can I prove the trim-out passed?", a: "Yes. A timestamped proof photo lands on every device and fixture — a record you can hand to the inspector or client." },
      { q: "How do I know they saw it?", a: "You see the moment the link opens and get pinged the second a proof photo comes back." },
      { q: "Can I send only the electrical scope?", a: "Yes. Sort & Send splits a mixed punch list so the electrician gets just their items." },
    ],
  },
  restoration: {
    title: "Restoration Proof-of-Work App with Photos | Send-Proof",
    eyebrow: "Mitigation · Repair · Proof Before Crews Leave",
    h1a: "Get proof before the crew leaves the ",
    h1b: "loss site.",
    sub: "Send each restoration crew their scope, require a proof photo before they leave mitigation or repair, and verify remotely — without driving to every loss.",
    subMuted: "Built for restoration contractors running water, fire and mold jobs across scattered sites.",
    desc: "Send-Proof for restoration: send each crew their scope, require photo proof before they leave the loss, and verify mitigation and repair remotely. No app for crews.",
    heroBullets: ["Proof before the crew leaves", "Photo record for the adjuster", "Verify every loss remotely"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "Scope the loss", d: "Mitigation, demo, dry-out, repair — add every item with a reference photo and note per room." },
      { t: "Photo before leaving", d: "The crew snaps annotated proof on every item on-site, so nothing's missed before they roll." },
      { t: "Verify remotely", d: "One link per crew or supervisor — you see it open and approve from anywhere." },
    ],
    sortTitle: "One loss scope in. Clean crew sends out.",
    sortSub: "A single loss spans mitigation, demo, dry-out and repair. Tap once and AI buckets every item to the right crew — matched to your vendors — so each gets only their scope with photos and a live checklist link for the file.",
    faq: [
      { q: "Do restoration crews need an app?", a: "No. They tap the link, see only their scope, snap photos on-site, and submit. Nothing to download." },
      { q: "Can I build a photo record for the adjuster?", a: "Yes. Every item gets a timestamped, annotated proof photo — a clean file for carriers and supervisors." },
      { q: "How do I verify a loss without driving there?", a: "The moment a crew opens the link you know it's in motion, and proof photos let you approve remotely." },
      { q: "Can I require proof before a crew leaves?", a: "That's the point — each item needs a photo, so nothing gets called done without visual proof on-site." },
    ],
  },
  multifamily: {
    title: "Multifamily Turn App with Photo Proof | Send-Proof",
    eyebrow: "Portfolio Turns · Photo Proof · Open Tracking",
    h1a: "Run turns across every property without ",
    h1b: "living in your truck.",
    sub: "Send each crew their units across every building, get photo proof per turn, and watch vacancy drop across the whole portfolio — without driving site to site.",
    subMuted: "Built for multifamily operators and regional managers running turns across multiple properties.",
    desc: "Send-Proof for multifamily: coordinate turns across every property, send each crew their units, and get photo proof back to cut vacancy portfolio-wide. No app for crews.",
    heroBullets: ["Turns across every building", "Photo proof per unit, per crew", "See each crew open its list"],
    stepImgs: ["/shots/jobdetail_mobile.webp", "/shots/annotate_mobile.webp", "/shots/share_mobile.webp"],
    stepCopy: [
      { t: "List turns by property", d: "Paint, clean, flooring, maintenance — add each unit's scope across every building with reference photos." },
      { t: "Photo each unit", d: "Each crew snaps annotated proof per unit and submits — no site visit to confirm." },
      { t: "Send per crew", d: "One link per crew or property — you see the moment it opens across the whole portfolio." },
    ],
    sortTitle: "Every property's turns in. Clean per-crew sends out.",
    sortSub: "Turns stacked across buildings and trades? Tap once and AI buckets paint, clean, flooring and maintenance to the right crew — matched to your vendors — so each gets only their units with photos and a live checklist link.",
    faq: [
      { q: "Can I run turns across multiple properties at once?", a: "Yes. Send each crew their units across every building from one place, and track opens and proof portfolio-wide." },
      { q: "Do turn crews need an app or account?", a: "No. Crews tap the link, see only their units, snap photos, and submit. No download, no login." },
      { q: "How does this cut vacancy days?", a: "You stop driving building to building. The moment a crew opens the link you know it's moving, and photo proof says a unit's ready to re-list." },
      { q: "How is this different from a single unit make-ready?", a: "Make-ready is one unit at a time; this coordinates crews and proof across your whole portfolio of properties." },
    ],
  },
};

export default function VerticalLanding({ vertical = "contractors" }) {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");
  const v = VERTICALS[vertical] || VERTICALS.contractors;

  useEffect(() => {
    const prevTitle = document.title;
    if (v.title) document.title = v.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta && v.desc) meta.setAttribute("content", v.desc);
    let script;
    if (v.faq?.length) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-vlanding-faq", "1");
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: v.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      });
      document.head.appendChild(script);
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
      if (script) script.remove();
    };
  }, [v]);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[1200px]">
        <div className="absolute left-1/2 top-[-200px] h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
        <div className="absolute right-[-160px] top-[560px] h-[440px] w-[440px] rounded-full bg-emerald-500/[0.06] blur-[130px]" />
      </div>
      <div className="relative z-10">
        {/* Nav */}
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="vlanding-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="whitespace-nowrap font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <div className="hidden items-center gap-2 sm:flex">
            <Button variant="outline" className="border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-white" data-testid="vlanding-signin-btn" onClick={() => navigate("/auth?mode=signin")}>Sign in</Button>
            <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="vlanding-nav-cta" onClick={go}>Start free trial</Button>
          </div>
          <div className="flex items-center gap-2 sm:hidden">
            <Button size="sm" className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="vlanding-nav-login-mobile" onClick={() => navigate("/auth?mode=signin")}>Sign in</Button>
          </div>
          </div>
        </header>

        {/* Hero */}
        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-6xl px-5 pt-10 sm:px-8 sm:pt-16">
          <div className="grid items-center gap-14 md:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-500">
                <Eye className="h-3.5 w-3.5" /> {v.eyebrow}
              </span>
              <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="vlanding-h1">
                {v.h1a}<span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">{v.h1b}</span>
              </h1>
              <p className="mt-4 max-w-md text-base text-muted-foreground sm:text-lg" data-testid="vlanding-definition">
                Send-Proof is a punch list app: you text your crew a link, get a photo on every item, and see the moment they open it.
              </p>
              <p className="mt-3 max-w-md font-heading text-lg font-bold leading-snug text-foreground sm:text-xl">
                See the second they open it. Get the photo when it's done.
              </p>
              <p className="mt-3 max-w-md text-base text-muted-foreground sm:text-lg">{v.sub}</p>
              <p className="mt-3 text-sm font-medium text-foreground/70">{v.subMuted}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button size="lg" className="h-12 bg-emerald-500 px-6 text-base hover:bg-emerald-600" data-testid="vlanding-hero-cta" onClick={go}>
                  Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">$9.99/mo after your 15-day trial · Cancel anytime</p>

              <a
                href={`/r/${SAMPLE_TOKEN}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vlanding-sample-cta"
                className="mt-6 flex items-center gap-3 rounded-2xl border border-border bg-[hsl(222,44%,12%)] p-4 transition-colors hover:border-emerald-500/50"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15">
                  <Eye className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">Open a sample report</div>
                  <div className="text-xs text-muted-foreground">See exactly what you send — no signup</div>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </a>
            </div>

            <div className="relative pb-6 md:pb-0">
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/20 blur-[100px]" />
              <div className="relative"><PhoneMock /></div>
            </div>
          </div>
        </motion.section>

        {/* Proof strip */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto mt-16 max-w-6xl px-5 sm:px-8">
          <div className="grid gap-4 sm:grid-cols-3">
            {v.heroBullets.map((b) => (
              <div key={b} className={`${panel} flex items-center gap-3 p-5`}>
                <Check className="h-5 w-5 shrink-0 text-emerald-500" />
                <span className="text-sm font-semibold">{b}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* How it works */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pt-24 sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Three steps, list to link</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {v.stepCopy.map((s, i) => {
              const Icon = [Check, Camera, Link2][i];
              return (
                <div key={s.t} className={`${panel} relative overflow-hidden p-5`} data-testid={`vstep-${i + 1}`}>
                  <div className="mx-auto w-[190px]">
                    <div className="rounded-[1.6rem] border border-border bg-[hsl(222,44%,10%)] p-1.5 shadow-xl ring-1 ring-white/5">
                      <div className="flex aspect-[10/19] items-center justify-center overflow-hidden rounded-[1.3rem] bg-[hsl(222,47%,8%)]">
                        <img src={v.stepImgs[i]} alt={s.t} loading="lazy" className="h-full w-full object-contain" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex items-center gap-3">
                    <span className="font-heading text-3xl font-extrabold text-emerald-500/30">{i + 1}</span>
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500 text-white"><Icon className="h-5 w-5" /></div>
                    <h3 className="font-heading text-lg font-bold">{s.t}</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* Sort & Send */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pt-24 sm:px-8">
          <div className="rounded-3xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6 shadow-2xl shadow-emerald-500/5 ring-1 ring-emerald-500/10 sm:p-10">
            <div className="grid items-center gap-10 md:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-400">
                  <Sparkles className="h-3.5 w-3.5" /> Sort &amp; Send
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight sm:text-3xl">{v.sortTitle}</h2>
                <p className="mt-3 max-w-md text-base text-muted-foreground">{v.sortSub}</p>
              </div>
              <div className="space-y-3">
                {[
                  { trade: "Electrical", vendor: "Bright Spark Co.", n: 6 },
                  { trade: "Plumbing", vendor: "FlowRight Plumbing", n: 4 },
                  { trade: "Paint", vendor: "TruCoat Painters", n: 5 },
                ].map((b) => (
                  <div key={b.trade} className="flex items-center gap-3 rounded-2xl border border-border bg-[hsl(222,44%,12%)] p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 font-heading text-sm font-bold text-emerald-400">{b.n}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{b.trade}</div>
                      <div className="truncate text-xs text-muted-foreground">{b.vendor}</div>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white"><Send className="h-3.5 w-3.5" /> Send</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Sample report */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pt-24 sm:px-8">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-500">This is what you send</span>
              <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">One link. The whole list, worked.</h2>
              <p className="mt-3 max-w-md text-base text-muted-foreground">
                No account, no app download for whoever you send it to. They tap the link and see every item, every photo, and a timestamp on each one.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-foreground">
                {["A photo on every completed item", "Notes & annotations drawn right on the photo", "A live receipt the moment your link is opened"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {f}</li>
                ))}
              </ul>
              <Button className="mt-6 bg-emerald-500 hover:bg-emerald-600" data-testid="vlanding-sample-cta-2" onClick={() => window.open(`/r/${SAMPLE_TOKEN}`, "_blank")}>
                Open the sample report <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="relative mx-auto w-[260px] sm:w-[300px]">
              <div className="rounded-[2.4rem] border border-border bg-[hsl(222,44%,10%)] p-2.5 shadow-2xl ring-1 ring-white/5">
                <div className="overflow-hidden rounded-[1.9rem] bg-[hsl(222,47%,8%)]">
                  <img src="/shots/share_mobile.webp" alt="The public punch-list report your client, GC or trade opens" className="block w-full" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Tracking timeline */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pt-24 sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">From sent to verified</h2>
          <p className="mt-2 max-w-lg text-base text-muted-foreground">Skip the "did you get it?" texts. Send the list, get photo proof back, verify it's done.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Send, t: "List sent", d: "You text the punch list. One tap opens it — nothing to download.", tag: "10:02 AM" },
              { icon: Camera, t: "Proof comes back", d: "Your crew photographs each item and sends it back. You get the ping the moment it lands.", tag: "4:12 PM" },
              { icon: Check, t: "You verify it's done", d: "Tap to verify each proof — now you've got a photo-backed record the work got done.", tag: "✅ Verified" },
            ].map(({ icon: Icon, t, d, tag }, i) => (
              <div key={t} className={`${panel} relative p-6`}>
                <div className="flex items-center justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400"><Icon className="h-5 w-5" /></div>
                  <span className="text-xs font-semibold text-emerald-400">{tag}</span>
                </div>
                <h3 className="mt-4 font-heading text-base font-bold">{t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{d}</p>
                {i < 2 && <ChevronRight className="absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-border sm:block" />}
              </div>
            ))}
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-3xl px-5 pt-24 sm:px-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Questions from the field</h2>
          <div className="mt-6 space-y-3">
            {v.faq.map((f) => (
              <details key={f.q} className={`${panel} group p-5`} data-testid="vlanding-faq-item">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                  <span className="font-heading text-base font-bold">{f.q}</span>
                  <ChevronRight className="h-5 w-5 shrink-0 text-emerald-500 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </motion.section>

        {/* Pricing */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-md px-5 pb-20 pt-24 sm:px-8">
          <div className="relative rounded-2xl border-2 border-emerald-500 bg-gradient-to-b from-[hsl(222,44%,14%)] to-[hsl(222,47%,9%)] p-7 shadow-2xl shadow-emerald-500/20" data-testid="vlanding-pricing-card">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-500">Everything, one plan</div>
            <div className="mt-2 flex items-end gap-1">
              <span className="font-heading text-5xl font-extrabold">$9.99</span>
              <span className="pb-2 text-muted-foreground">/month</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">15-day free trial. Cancel anytime.</p>
            <ul className="mt-5 space-y-2 text-sm text-foreground">
              {["Unlimited jobs & reports", "Unlimited photos", "Real-time open tracking", "Shareable proof links + QR"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {f}</li>
              ))}
            </ul>
            <Button className="mt-6 h-12 w-full bg-emerald-500 text-base hover:bg-emerald-600" data-testid="vlanding-pricing-cta" onClick={go}>
              Start 15-day free trial
            </Button>
          </div>
        </motion.section>

        {/* More trades — internal links for SEO crawl depth (not in the shared footer) */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-6xl px-5 pb-4 sm:px-8" data-testid="vlanding-trades">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Send-Proof for every trade</h2>
          <p className="mt-2 max-w-lg text-base text-muted-foreground">Same workflow, tuned to how you close out work.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {TRADE_LINKS.map((tl) => (
              <Link key={tl.to} to={tl.to} className={`${panel} flex items-center justify-between gap-3 p-4`} data-testid={`vlanding-trade-${tl.to.replace(/\//g, "")}`}>
                <span className="text-sm font-semibold">{tl.label}</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-emerald-500" />
              </Link>
            ))}
          </div>
        </motion.section>

        {/* Footer */}
        <SiteFooter />
      </div>
    </div>
  );
}
